import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Upload,
  CheckCircle,
  Briefcase,
  Heart,
  TrendingUp,
  Users,
  Sparkles,
  FileText,
  X,
} from "lucide-react";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareersPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    message: "",
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Please upload a PDF or Word document");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      setResume(file);
    }
  };

  const removeFile = () => {
    setResume(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("phone", formData.phone || "");
      formDataToSend.append("position", formData.position || "");
      formDataToSend.append("experience", formData.experience || "");
      formDataToSend.append("message", formData.message || "");
      if (resume) {
        formDataToSend.append("resume", resume);
      }

      await axios.post(`${API}/careers/apply`, formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="font-['Plus_Jakarta_Sans']" data-testid="careers-page">
        <section className="py-32 bg-slate-50">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-20 h-20 rounded-full bg-[#2A9D8F] flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mb-4 font-['Outfit']">
              Application Received!
            </h1>
            <p className="text-slate-600 text-lg mb-8">
              Thank you for your interest in joining Momentum VA. We'll review your 
              application and get back to you within 5 business days.
            </p>
            <Button
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  name: "",
                  email: "",
                  phone: "",
                  position: "",
                  experience: "",
                  message: "",
                });
                setResume(null);
              }}
              className="bg-[#0B1C3E] hover:bg-[#0B1C3E]/90 text-white rounded-full px-6"
              data-testid="submit-another-btn"
            >
              Submit Another Application
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="font-['Plus_Jakarta_Sans']" data-testid="careers-page">
      <SEO
        title="Careers"
        description="Join Momentum VA — career opportunities for virtual assistants supporting Australian real estate offices. Competitive pay, career growth, and a supportive team culture."
        path="/careers"
      />
      <section className="relative bg-[#0B1C3E] py-20 lg:py-28" data-testid="careers-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-[#E86C3F] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-72 h-72 bg-[#2A9D8F] rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Join Our Team
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-6 font-['Outfit']">
              Build Your Career with Momentum VA
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              We're looking for motivated individuals who want to grow their careers 
              in real estate support. No experience required—just the right attitude 
              and willingness to learn.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
              Why Join Us
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] mt-4 font-['Outfit']">
              More Than Just a Job
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              At Momentum VA, we invest in our people. We believe in training, 
              growth, and building long-term careers—not just filling positions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#2A9D8F]/30 transition-colors group card-hover" data-testid="benefit-0">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Growth Opportunities</h3>
              <p className="text-slate-600">Continuous training and clear career progression paths.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#2A9D8F]/30 transition-colors group card-hover" data-testid="benefit-1">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Supportive Culture</h3>
              <p className="text-slate-600">A team that values collaboration and celebrates success.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#2A9D8F]/30 transition-colors group card-hover" data-testid="benefit-2">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Real Estate Expertise</h3>
              <p className="text-slate-600">Learn the ins and outs of Australian property management.</p>
            </div>

            <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:border-[#2A9D8F]/30 transition-colors group card-hover" data-testid="benefit-3">
              <div className="w-14 h-14 rounded-xl bg-[#0B1C3E] flex items-center justify-center mb-6 group-hover:bg-[#2A9D8F] transition-colors">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-[#0B1C3E] mb-3 font-['Outfit']">Work-Life Balance</h3>
              <p className="text-slate-600">Structured schedules aligned with Australian business hours.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Culture Image Section */}
      <section className="py-20 bg-slate-50" data-testid="culture-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_momentum-admin/artifacts/2p4nk0fd_DSC06042.jpg"
                alt="Momentum VA team culture"
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <span className="text-[#2A9D8F] font-semibold text-sm uppercase tracking-wider">
                Our Culture
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] font-['Outfit']">
                What We Look For
              </h2>
              <p className="text-slate-600 text-lg">
                We don't require prior real estate experience. What matters most is 
                your attitude, your willingness to learn, and your commitment to 
                delivering quality work.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-700">Positive, can-do attitude</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-700">Strong attention to detail</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-700">Excellent communication skills</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-700">Eagerness to learn and grow</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#2A9D8F] flex-shrink-0" />
                  <span className="text-slate-700">Reliability and accountability</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 md:py-28 bg-white" data-testid="application-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-[#E86C3F]/10 rounded-full mb-6">
              <Briefcase className="w-5 h-5 text-[#E86C3F] mr-2" />
              <span className="text-[#E86C3F] font-semibold text-sm">Apply Now</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0B1C3E] font-['Outfit']">
              Start Your Application
            </h2>
            <p className="text-slate-600 mt-4 text-lg">
              Fill out the form below and we'll be in touch within 5 business days.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-slate-50 rounded-2xl p-8 border border-slate-100"
            data-testid="application-form"
          >
            <div className="space-y-6">
              {/* Name & Email */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-[#0B1C3E] font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                    placeholder="Your full name"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-[#0B1C3E] font-medium">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                    placeholder="your@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {/* Phone & Position */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-[#0B1C3E] font-medium">
                    Phone Number
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                    placeholder="+63 XXX XXX XXXX"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="position" className="text-[#0B1C3E] font-medium">
                    Position of Interest
                  </Label>
                  <Select
                    value={formData.position}
                    onValueChange={(value) => handleSelectChange("position", value)}
                  >
                    <SelectTrigger
                      className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                      data-testid="select-position"
                    >
                      <SelectValue placeholder="Select a position" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Property Management VA">Property Management VA</SelectItem>
                      <SelectItem value="Sales Support VA">Sales Support VA</SelectItem>
                      <SelectItem value="General Admin VA">General Admin VA</SelectItem>
                      <SelectItem value="Team Leader">Team Leader</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Experience */}
              <div>
                <Label htmlFor="experience" className="text-[#0B1C3E] font-medium">
                  Experience Level
                </Label>
                <Select
                  value={formData.experience}
                  onValueChange={(value) => handleSelectChange("experience", value)}
                >
                  <SelectTrigger
                    className="mt-2 h-12 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                    data-testid="select-experience"
                  >
                    <SelectValue placeholder="Select your experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Entry Level (No experience required)">Entry Level (No experience required)</SelectItem>
                    <SelectItem value="1-2 years">1-2 years</SelectItem>
                    <SelectItem value="3-5 years">3-5 years</SelectItem>
                    <SelectItem value="5+ years">5+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Resume Upload */}
              <div>
                <Label className="text-[#0B1C3E] font-medium">
                  Resume / CV
                </Label>
                <div className="mt-2">
                  {resume ? (
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-8 h-8 text-[#2A9D8F]" />
                        <div>
                          <p className="font-medium text-[#0B1C3E]">{resume.name}</p>
                          <p className="text-sm text-slate-500">
                            {(resume.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                        data-testid="remove-file-btn"
                      >
                        <X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="resume"
                      className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#2A9D8F] transition-colors"
                    >
                      <Upload className="w-10 h-10 text-slate-400 mb-3" />
                      <p className="text-slate-600 font-medium">
                        Click to upload your resume
                      </p>
                      <p className="text-slate-400 text-sm mt-1">
                        PDF or Word document (max 5MB)
                      </p>
                      <input
                        ref={fileInputRef}
                        id="resume"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="input-resume"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="text-[#0B1C3E] font-medium">
                  Why do you want to join Momentum VA?
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="mt-2 border-slate-200 focus:border-[#0B1C3E] focus:ring-[#0B1C3E]"
                  placeholder="Tell us a bit about yourself and why you're interested..."
                  data-testid="input-message"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#E86C3F] hover:bg-[#d65a2e] text-white rounded-full py-6 text-base font-semibold shadow-lg"
                data-testid="submit-application-btn"
              >
                {isSubmitting ? "Submitting..." : "Submit Application"}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
